import { Grid, Stack } from "@mui/material";
import { max } from "lodash";
import { useTranslation } from "react-i18next";

import { IsAllowed } from "../../acl";
import { downloadCertificateFiles } from "../../api/functions/all";
import { useCheckCertificatesDownloadFilesStatus, useGetBuildingEPCInfo } from "../../api/hooks/allHooks";
import { eventNames, withGAEventTracking } from "../../functions/googleAnalytics";
import { isNumeric } from "../../utils/dataManipulation";
import { dateDifference, dateDifferenceString } from "../../utils/date";
import { generateAxisValues } from "../../utils/dynamicDashboard";
import { valueOrAlternative } from "../../utils/namesManipulation";
import { cn } from "../../utils/styling";
import { BulletChartDoubleWithIntervals } from "../chart/BulletChart";
import { ComponentsCommonHeader } from "../ComponentsCommonHeader";
import { DataCard } from "../DataCard";
import { EnergyEfficiencyClass } from "../EnergyEfficiencyClass";
import { TextErrorBoundary } from "../ErrorBoundary";
import { LabelValueInline } from "../LabelValueInline";
import { EPCPerformanceRatioSection } from "./EPCPerformanceRatioSection";

type EPCPartProps = {
  projectId: string;
};

export const EPCPart = ({ projectId }: EPCPartProps) => {
  const { t } = useTranslation();
  const { data: downloadFileExistenceStatus } = useCheckCertificatesDownloadFilesStatus(projectId, "epc");

  const { data: epcInfoData } = useGetBuildingEPCInfo(projectId);
  let epcIsExpired = false;
  let epcValidDaysValueClassName = "";
  const epcInfoAll: any = epcInfoData?.length ? epcInfoData[0] : {};
  if (Object.keys(epcInfoAll)?.length) {
    try {
      const today = new Date();
      epcInfoAll.epc_valid_days = dateDifferenceString(dateDifference(epcInfoAll?.valid_until, today));
      epcIsExpired = new Date(epcInfoAll?.valid_until) < today;
      if (epcIsExpired) {
        epcInfoAll.epc_valid_days = `-${epcInfoAll.epc_valid_days}`;
        epcValidDaysValueClassName = cn(epcIsExpired && "text-lds-purpose-status-negative");
      }
    } catch {
      // Do Nothing
    }
  }
  const bullets = [
    {
      color: "var(--color-foreground-primary)",
      handleHeight: 0,
      label: `${t("BuildingProfile.kpis.primaryEnergyDemand")}: ${epcInfoAll?.primary_energy_kwh_per_m2}`,
      reverse: true,
      value: epcInfoAll?.primary_energy_kwh_per_m2,
    },
    {
      color: "var(--color-foreground-muted)",
      handleHeight: 40,
      label: `${t("BuildingProfile.kpis.primaryEnergyDemandReferenceBuilding")}: ${
        epcInfoAll?.primary_energy_reference_kwh_per_m2
      }`,
      reverse: true,
      value: epcInfoAll?.primary_energy_reference_kwh_per_m2,
    },
    {
      color: "var(--color-foreground-primary)",
      handleHeight: 0,
      label: `${t("BuildingProfile.kpis.endEnergyDemand")}: ${epcInfoAll?.end_energy_kwh_per_m2}`,
      reverse: false,
      value: epcInfoAll?.end_energy_kwh_per_m2,
    },
    {
      color: "var(--color-foreground-muted)",
      handleHeight: 40,
      label: `${t("BuildingProfile.kpis.endEnergyDemandReferenceBuilding")}: ${
        epcInfoAll?.end_energy_reference_kwh_per_m2
      }`,
      reverse: false,
      value: epcInfoAll?.end_energy_reference_kwh_per_m2,
    },
  ];

  let bulletsMaxValue =
    Math.max(
      epcInfoAll?.primary_energy_kwh_per_m2,
      epcInfoAll?.primary_energy_reference_kwh_per_m2,
      epcInfoAll?.end_energy_kwh_per_m2,
      epcInfoAll?.end_energy_reference_kwh_per_m2
    ) ?? 0;

  if (!isNumeric(bulletsMaxValue)) bulletsMaxValue = 0;

  const defaultEnergyAxis = [0, 50, 100, 150, 200, 250, 300, 350, 400, 450, 500, 550];

  const classification = epcInfoAll?.classification;
  let energyAxis = defaultEnergyAxis;
  let maxEnergyValue = 600;

  let thresholds = [];
  let labels = [];
  let minEnergyValue;
  let fMax;

  const labelsKeysAndTitles: { key: string; title: string }[] = [
    { key: "a_plus", title: "A+" },
    { key: "a", title: "A" },
    { key: "b", title: "B" },
    { key: "c", title: "C" },
    { key: "d", title: "D" },
    { key: "e", title: "E" },
    { key: "f", title: "F" },
    { key: "g", title: "G" },
    { key: "h", title: "H" },
  ];

  const labelsMapping: Record<string, string> = {};
  labelsKeysAndTitles.forEach((label) => {
    labelsMapping[label.key] = label.title;
  });

  if (classification) {
    const activeClassesNonZero = labelsKeysAndTitles.filter((item) => classification[`class_${item.key}_min`]);
    const lastActiveClass = activeClassesNonZero[activeClassesNonZero?.length - 1];
    maxEnergyValue = classification?.[`class_${lastActiveClass?.key}_min`] * 1.2;
    const maxAxisValue = Math.max(maxEnergyValue, bulletsMaxValue);
    energyAxis = !isNumeric(maxAxisValue) ? defaultEnergyAxis : generateAxisValues(0, maxAxisValue, 50) ?? [];
    for (const className in labelsMapping) {
      const isLastActiveClass = className === lastActiveClass?.key;
      const classMin = classification[`class_${className}_min`];
      const classMax = classification[`class_${className}_max`];

      if (!classMin && !classMax) continue;

      if (className === "f") {
        fMax = classMax;
      }
      if (minEnergyValue === undefined) {
        minEnergyValue = classMin;
        thresholds.push(classMin);
      }
      if (classMax && !isLastActiveClass) {
        labels.push(labelsMapping[className]);
        thresholds.push(classMax);
      }
      if (classMax && isLastActiveClass) {
        labels.push(labelsMapping[className]);
      }
      if (classMin && !classMax) {
        labels.push(labelsMapping[className]);
      }
      if (isLastActiveClass) break;
    }
    if (maxEnergyValue > max(thresholds)) thresholds.push(maxEnergyValue);
  }

  const epcInfoTextConfig = [
    { dataKey: "epc_number", label: "EPC Number" },
    { dataKey: "issued_on", label: "EPC Issued On" },
    { dataKey: "type", label: "EPC Type" },
    { dataKey: "valid_until", label: "EPC Valid Until" },
    { dataKey: "according_to", label: "EPC According To" },
    { dataKey: "epc_valid_days", label: "EPC Valid Days", valueClassName: epcValidDaysValueClassName },
    { dataKey: "class_defined_via", label: "Class Defined With" },
    { dataKey: "issued_by", label: "EPC Issued By" },
  ];
  const epcInfoTextAndValues = epcInfoTextConfig.map((item) => ({ ...item, value: epcInfoAll?.[item.dataKey] }));

  const maxAxisValue = Math.max(...(energyAxis ?? []));
  let fMaxToMax = 1;
  try {
    fMaxToMax = fMax / maxAxisValue;
  } catch {
    // Do Nothing
  }

  const colorbarScaleColors = [
    { color: "#1E955B", threshold: 0 },
    { color: "#51AB4A", threshold: 14 },
    { color: "#CADC09", threshold: 28 },
    { color: "#FFFD0A", threshold: 42 },
    { color: "#FDF400", threshold: 56 },
    { color: "#FAA112", threshold: 70 },
    { color: "#FE553D", threshold: 90 },
    // { color: '#F94D48', threshold: 90 },
    { color: "#F00", threshold: 100 },
  ];

  let colorbarScaleRelativeColors = colorbarScaleColors.map((item) => ({
    ...item,
    threshold: item.threshold * fMaxToMax,
  }));
  if (labels?.includes(labelsMapping["g"])) {
    try {
      const gTreshold = classification[`class_g_max`] / maxAxisValue;
      colorbarScaleRelativeColors.push({ color: "#F00", threshold: Math.min(gTreshold, 1) * 100 });
    } catch {
      // Do Nothing
    }
  }

  if (labels?.includes(labelsMapping["h"])) {
    try {
      const hMinTreshold = classification[`class_h_min`] / maxAxisValue;
      const hMaxTreshold = classification[`class_h_max`] / maxAxisValue;
      colorbarScaleRelativeColors.push({ color: "#e30202", threshold: hMinTreshold * 100 });
      colorbarScaleRelativeColors.push({ color: "#e30202", threshold: Math.min(hMaxTreshold, 1) * 100 });
    } catch {
      // Do Nothing
    }
  }

  const colorbarScale =
    "linear-gradient(to right " +
    colorbarScaleRelativeColors.reduce((total, curr) => total + `, ${curr.color} ${curr.threshold}% `, " ") +
    ")";

  return (
    <TextErrorBoundary>
      <div>
        <ComponentsCommonHeader
          action={() =>
            withGAEventTracking(eventNames.BP_DOWNLOAD_CERTIFICATE, { certificateType: "epc" }, () =>
              downloadCertificateFiles({ certificateType: "epc", projectId })
            )
          }
          actionIsDisabled={downloadFileExistenceStatus?.status !== 200}
          actionText={t("Certificates.cards.buttonShort") as string}
          className="!pb-4 !pt-0"
          hasActionAccess={IsAllowed("CERTIFICATE.DOWNLOAD_FILES")}
          title={t("BuildingProfile.certificateCard.epcTitle") as string}
        />
        <Stack className="flex-col items-start pb-15">
          <Grid className="items-start justify-between" columnSpacing={4} container rowSpacing={6}>
            {epcInfoTextAndValues?.map((item, i) => {
              return (
                <Grid item key={i} xs={6}>
                  <LabelValueInline
                    className=""
                    label={item.label}
                    labelProps={{ className: "text-lds-foreground-muted" }}
                    value={valueOrAlternative(item.value, t("general.noData"))}
                    valueProps={{ className: item.valueClassName }}
                  />
                </Grid>
              );
            })}
          </Grid>
          <Grid className="mt-8 w-full" columnSpacing={4} container rowSpacing={6}>
            {isNumeric(epcInfoAll?.epc_carbon_dioxide_emission) && (
              <Grid item xs={4}>
                <DataCard
                  kpi={{
                    name: t("BuildingProfile.card.CO2 EMISSIONS") ?? "",
                    unit: "KgCO₂",
                    value: epcInfoAll?.epc_carbon_dioxide_emission,
                  }}
                />
              </Grid>
            )}
            {isNumeric(epcInfoAll?.primary_energy_kwh_per_m2) && (
              <Grid item xs={4}>
                <DataCard
                  kpi={{
                    name: t("BuildingProfile.kpis.primaryEnergyConsumption") ?? "",
                    unit: "kWh",
                    value: epcInfoAll?.primary_energy_kwh_per_m2,
                  }}
                />
              </Grid>
            )}
            <Grid item xs={4}>
              <EnergyEfficiencyClass
                className="h-full min-h-[10rem] w-50"
                energyClass={epcInfoAll?.class}
                label={t("BuildingProfile.kpis.energyEfficiencyClass") ?? ""}
              />
            </Grid>
          </Grid>
        </Stack>
        <BulletChartDoubleWithIntervals
          axis={energyAxis}
          bulletColorBarClassName="barchart-new-bg-gradient-energy-performance"
          bulletColorBarStyle={{ background: colorbarScale }}
          bullets={bullets}
          className="mx-8 my-16"
          isLoading={false}
          labels={labels}
          thresholds={thresholds}
        />
        <EPCPerformanceRatioSection epcInfo={epcInfoAll} />
      </div>
    </TextErrorBoundary>
  );
};
